import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { API_URL } from '../utils/api';

interface ProcessStep {
  id: string;
  step: string;
  title: string;
  icon: string;
  gradient: string;
}

interface HowWeWorkData {
  badgeText: string;
  badgeIcon: string;
  title: string;
  subtitle: string;
  steps: ProcessStep[];
  ctaText: string;
  ctaLink: string;
  visible: boolean;
}

// ============================================
// SKELETON
// ============================================

const HowWeWorkSkeleton = () => (
  <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
    <div className="max-w-6xl mx-auto space-y-12">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="h-8 w-36 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="h-8 bg-gray-200 rounded w-72 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-96 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>

      {/* Steps grid — 5 cols matching default data */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="bg-white/80 rounded-2xl p-6 shadow-lg border border-white/60 space-y-4">
            {/* Icon */}
            <div className="w-12 h-12 bg-gray-200 rounded-xl mx-auto relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            {/* Step number */}
            <div className="h-7 w-10 bg-gray-200 rounded mx-auto relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            {/* Title */}
            <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
        ))}
      </div>

      {/* CTA button */}
      <div className="flex justify-center">
        <div className="h-12 w-52 bg-gray-200 rounded-full relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
    </div>
  </section>
);

// ============================================
// COMPONENT
// ============================================

export function HowWeWorkSection() {
  const [howWeWorkData, setHowWeWorkData] = useState<HowWeWorkData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHowWeWorkData();
  }, []);

  const fetchHowWeWorkData = async () => {
    try {
      const timestamp = new Date().getTime();
      const response = await fetch(`${API_URL}/homepage/how-we-work?t=${timestamp}`, {
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) {
        setDefaultData();
        return;
      }

      const result = await response.json();
      if (result.success && result.data) {
        setHowWeWorkData(result.data);
      } else {
        setDefaultData();
      }
    } catch (error: any) {
      setDefaultData();
    } finally {
      setLoading(false);
    }
  };

  const setDefaultData = () => {
    setHowWeWorkData({
      badgeText: 'How We Work',
      badgeIcon: 'Workflow',
      title: 'Our Simple 5-Step Process',
      subtitle: 'Predictable timeline, exceptional results—delivered on time and on budget',
      steps: [
        { id: 'step-1', step: '01', title: 'Discovery', icon: 'Target', gradient: 'from-[#2F2E5C] to-[#4a487a]' },
        { id: 'step-2', step: '02', title: 'Concept', icon: 'FileText', gradient: 'from-[#D74444] to-[#e85e5e]' },
        { id: 'step-3', step: '03', title: 'Production', icon: 'Cog', gradient: 'from-[#E87D3E] to-[#E8B84D]' },
        { id: 'step-4', step: '04', title: 'Review', icon: 'Eye', gradient: 'from-[#2F2E5C] to-[#4a487a]' },
        { id: 'step-5', step: '05', title: 'Delivery', icon: 'Check', gradient: 'from-[#D74444] to-[#e85e5e]' }
      ],
      ctaText: 'View Full Process Details',
      ctaLink: '/process',
      visible: true
    });
  };

  const getIconComponent = (iconName: string) => {
    const IconComponent = (LucideIcons as any)[iconName];
    return IconComponent || LucideIcons.Circle;
  };

  // Show skeleton while loading
  if (loading) {
    return <HowWeWorkSkeleton />;
  }

  // Don't render if not visible or no data
  if (!howWeWorkData?.visible) return null;

  const BadgeIcon = getIconComponent(howWeWorkData.badgeIcon);
  const stepCount = howWeWorkData.steps.length;

  const getGridCols = () => {
    if (stepCount <= 3) return 'grid-cols-2 sm:grid-cols-3';
    if (stepCount === 4) return 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-4';
    if (stepCount === 5) return 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5';
    return 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';
  };

  return (
    <section
      id="how-we-work"
      className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden"
    >
      {/* Premium Matte Background with Gradient Overlays */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-[#2F2E5C] via-[#4a487a] to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-tl from-[#E87D3E] via-[#E8B84D] to-transparent rounded-full blur-3xl" />
      </div>

      {/* Subtle Matte Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.01]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
            <BadgeIcon className="w-4 h-4 text-[#E87D3E]" />
            <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
              {howWeWorkData.badgeText}
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-[#2F2E5C]">
            {howWeWorkData.title}
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {howWeWorkData.subtitle}
          </p>
        </div>

        <div className={`grid ${getGridCols()} gap-4 sm:gap-6`}>
          {howWeWorkData.steps.map((item) => {
            const StepIcon = getIconComponent(item.icon);
            return (
              <div key={item.id} className="text-center group">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/60 hover:shadow-2xl hover:scale-105 transition-all duration-300 relative overflow-hidden">
                  <div className="absolute inset-0 opacity-[0.02]" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, #2F2E5C 1px, transparent 0)', backgroundSize: '30px 30px'}} />

                  <div className="relative z-10">
                    <div className={`w-12 h-12 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center mx-auto mb-4 text-white shadow-lg group-hover:shadow-xl transition-shadow`}>
                      <StepIcon className="w-5 h-5" />
                    </div>

                    <div className={`text-2xl font-bold mb-2 bg-gradient-to-r ${item.gradient} bg-clip-text text-transparent`}>
                      {item.step}
                    </div>

                    <h3 className="font-bold text-[#2F2E5C]">{item.title}</h3>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {howWeWorkData.ctaText && howWeWorkData.ctaLink && (
          <div className="text-center mt-10">
            <Link to={howWeWorkData.ctaLink}>
              <button className="bg-gradient-to-r from-[#2F2E5C] to-[#4a487a] hover:from-[#1a1935] hover:to-[#2F2E5C] text-white rounded-full px-8 h-12 gap-2 shadow-xl group transition-all duration-300 inline-flex items-center justify-center font-semibold">
                {howWeWorkData.ctaText}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}